import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import api from '../services/api';
import type { User } from '../types';

interface AuthContextType {
  user: User | null;
  login: (username: string, password: string) => Promise<User>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  isAuthenticated: boolean;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // ASK THE SERVER, ALWAYS — do not infer a session from localStorage.
  //
  // This used to skip the call entirely when localStorage held no token, which
  // was right while the token WAS the session. It stopped being right the moment
  // the products moved to their own hostnames: localStorage is origin-scoped, so
  // opening beats.lumnian.com after signing in at app.lumnian.com found an empty
  // store and showed the login page — while the `.lumnian.com` session cookie
  // was present and valid the whole time. The single sign-on worked; the app
  // never asked.
  //
  // `probeSession` never redirects, so this is safe on /login too.
  useEffect(() => {
    api
      .probeSession()
      .then((me) => {
        setUser(me);
        if (!me) api.setToken(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const login = async (username: string, password: string): Promise<User> => {
    await api.login(username, password);
    const userData = await api.getMe();
    setUser(userData);
    return userData;
  };

  const logout = async (): Promise<void> => {
    await api.logout();
    setUser(null);
  };

  const refreshUser = async (): Promise<void> => {
    const userData = await api.getMe();
    setUser(userData);
  };

  const value: AuthContextType = {
    user,
    login,
    logout,
    refreshUser,
    isAuthenticated: !!user,
    loading,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
